import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { GoogleSignInButton, useGoogleAuth } from '@questlabs/auth-js';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiFeather, FiMail, FiLock, FiArrowRight } = FiIcons;

const Login = () => {
  const { user: ncbUser, loading: ncbLoading, login } = useAuth();
  const { user: googleUser, initialized: googleInit, loading: googleLoading } = useGoogleAuth();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isLoading = ncbLoading || !googleInit || googleLoading;
  const isSignedIn = !!ncbUser || !!googleUser;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-light flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-accent-gold/20 border-t-accent-gold rounded-full animate-spin" />
          <p className="text-text-muted text-sm font-bold uppercase tracking-widest">Loading...</p>
        </div>
      </div>
    );
  }

  if (isSignedIn) {
    return <Navigate to={from} replace />;
  }

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err?.message || 'Login failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-light flex items-center justify-center px-4 py-20">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-brand-dark p-8 md:p-10">
        {/* Brand header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <SafeIcon icon={FiFeather} className="text-accent-gold w-8 h-8" />
            <h1 className="font-serif text-2xl font-bold text-accent-navy">OneFinalLegacy</h1>
          </div>
          <p className="text-text-muted text-sm leading-relaxed">
            Sign in to manage memorials, track tributes, and access your personal dashboard.
          </p>
        </div>

        {/* Google sign-in */}
        <div className="flex justify-center mb-6">
          <GoogleSignInButton />
        </div>

        <div className="relative flex py-2 items-center mb-6">
          <div className="flex-grow border-t border-brand-dark/30" />
          <span className="flex-shrink-0 mx-4 text-text-muted text-xs font-bold uppercase tracking-widest">or</span>
          <div className="flex-grow border-t border-brand-dark/30" />
        </div>

        {/* Email sign-in */}
        <form onSubmit={handleEmailLogin} className="space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-accent-navy mb-1.5">Email</label>
            <div className="relative">
              <SafeIcon icon={FiMail} className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-brand-dark/30 text-accent-navy text-sm focus:outline-none focus:ring-2 focus:ring-accent-gold/40 focus:border-accent-gold/50 bg-brand-light/30 transition-all"
                placeholder="you@example.com"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-accent-navy mb-1.5">Password</label>
            <div className="relative">
              <SafeIcon icon={FiLock} className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-brand-dark/30 text-accent-navy text-sm focus:outline-none focus:ring-2 focus:ring-accent-gold/40 focus:border-accent-gold/50 bg-brand-light/30 transition-all"
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 text-xs font-medium rounded-xl px-4 py-3">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex items-center justify-center gap-2 bg-accent-navy text-white py-3 rounded-xl font-bold text-sm hover:bg-accent-navy/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                Sign In <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
              </>
            )}
          </button>
        </form>

        <p className="mt-6 text-center text-[10px] text-text-muted leading-relaxed">
          By signing in, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
};

export default Login;